/* eslint-disable @typescript-eslint/no-explicit-any */
import { ApiBack } from '../axios-config';
import { AuthService } from '../auth/AuthService';

import {
	IActivity,
	INewActivityFormData,
	IResponsible,
} from '../../../components/custom/ActionPlans/NewActivityForm';

interface IActivityBackendPayload {
	descricao_atividade?: string;
	responsavel_procedimento?: string;
	prazo_previsto_inicio?: string;
	prazo_previsto_final?: string;
	prazo_real_inicio?: string;
	prazo_real_final?: string;
	observacoes_gerais?: string;
	finalizado?: 'TRUE' | 'FALSE';
}

const mapActivityFromBackend = (data: any): IActivity => {
	const responsibleData: IResponsible = {
		nome: '',
		matricula: '',
		unidadeOrganizacional: '',
		uuid: data.responsavel_procedimento,
	};

	return {
		id: data.uuid,
		descricaoAtividade: data.descricao_atividade,
		responsavel: responsibleData,
		prazoPrevistoInicio: data.prazo_previsto_inicio,
		prazoPrevistoFim: data.prazo_previsto_final,
		prazoRealInicio: data.prazo_real_inicio,
		prazoRealFim: data.prazo_real_final,
		observacoesGerais: data.observacoes_gerais,
		finalizada:
			typeof data.finalizado === 'boolean'
				? data.finalizado
				: typeof data.finalizado === 'string' &&
				data.finalizado.toUpperCase() === 'TRUE',
	};
};

const getAll = async (
	uuidDoPlanoDeAcao: string
): Promise<any | Error> => {
	await AuthService.verify();
	try {
		const token = await AuthService.getAccessToken();
		const response = await ApiBack.get<any[]>(
			`/tarefas-plano?uuid=${uuidDoPlanoDeAcao}`,
			{
				headers: {
					Authorization: `${token}`,
				},
			}
		);

		if (response.data && Array.isArray(response.data)) {
			return response.data;
		}

		return [];
	} catch (error) {
		return new Error(
			(error as { message: string }).message ||
			'Erro ao listar atividades do plano de ação.'
		);
	}
};

const createActivity = async (
	uuidDoPlanoDeAcao: string,
	data: INewActivityFormData
): Promise<IActivity | Error> => {
	await AuthService.verify();
	try {
		const token = await AuthService.getAccessToken();

		const payload: IActivityBackendPayload = {
			descricao_atividade: data.descricaoAtividade,
			responsavel_procedimento: data.responsavelUuid,
			prazo_previsto_inicio: data.prazoPrevistoInicio,
			prazo_previsto_final: data.prazoPrevistoFim,
			prazo_real_inicio: data.prazoRealInicio || '',
			prazo_real_final: data.prazoRealFim || '',
			observacoes_gerais: data.observacoesGerais || '',
		};

		if (!payload.responsavel_procedimento) {
			throw new Error(
				'UUID do responsável é obrigatório para criar a atividade.'
			);
		}

		const response = await ApiBack.post<any>(
			`/tarefa?uuid=${uuidDoPlanoDeAcao}`,
			JSON.stringify(payload),
			{
				headers: {
					'Content-Type': 'application/json',
					Authorization: `${token}`,
				},
			}
		);

		if (response.data) {
			return mapActivityFromBackend(response.data);
		}

		return new Error('Erro ao criar atividade: Resposta sem dados.');
	} catch (error) {
		return new Error(
			(error as { message: string }).message ||
			'Erro ao criar atividade do plano de ação.'
		);
	}
};

const updateActivity = async (
	uuidDaAtividade: string,
	dataToUpdate: Partial<IActivity>
): Promise<IActivity | Error> => {
	await AuthService.verify();
	try {
		const token = await AuthService.getAccessToken();

		const payload: Partial<IActivityBackendPayload> = {};

		if (dataToUpdate.descricaoAtividade !== undefined)
			payload.descricao_atividade = dataToUpdate.descricaoAtividade;

		if (dataToUpdate.responsavel?.uuid !== undefined) {
			payload.responsavel_procedimento = dataToUpdate.responsavel.uuid;
		}

		if (dataToUpdate.prazoPrevistoInicio !== undefined)
			payload.prazo_previsto_inicio = dataToUpdate.prazoPrevistoInicio;
		if (dataToUpdate.prazoPrevistoFim !== undefined)
			payload.prazo_previsto_final = dataToUpdate.prazoPrevistoFim;
		if (dataToUpdate.prazoRealInicio !== undefined)
			payload.prazo_real_inicio = dataToUpdate.prazoRealInicio;
		if (dataToUpdate.prazoRealFim !== undefined)
			payload.prazo_real_final = dataToUpdate.prazoRealFim;
		if (dataToUpdate.observacoesGerais !== undefined)
			payload.observacoes_gerais = dataToUpdate.observacoesGerais;

		if (dataToUpdate.finalizada !== undefined) {
			payload.finalizado = dataToUpdate.finalizada ? 'TRUE' : 'FALSE';
		}

		const response = await ApiBack.put<any>(
			`/tarefa?uuid=${uuidDaAtividade}`,
			JSON.stringify(payload),
			{
				headers: {
					'Content-Type': 'application/json',
					Authorization: `${token}`,
				},
			}
		);

		if (response.data) {
			return mapActivityFromBackend(response.data);
		} else {

			const defaultResponsible: IResponsible = {
				nome: '',
				matricula: '',
				unidadeOrganizacional: '',
				uuid: '',
			};

			const updatedResponsible: IResponsible = Object.assign(
				{},
				defaultResponsible,
				dataToUpdate.responsavel
			);

			return {
				...dataToUpdate,
				id: uuidDaAtividade,
				descricaoAtividade: dataToUpdate.descricaoAtividade || '',
				responsavel: updatedResponsible,
				prazoPrevistoInicio: dataToUpdate.prazoPrevistoInicio || '',
				prazoPrevistoFim: dataToUpdate.prazoPrevistoFim || '',
				prazoRealInicio: dataToUpdate.prazoRealInicio || '',
				prazoRealFim: dataToUpdate.prazoRealFim || '',
				observacoesGerais: dataToUpdate.observacoesGerais || '',
				finalizada: dataToUpdate.finalizada ?? false,
			};
		}
	} catch (error) {
		return new Error(
			(error as { message: string }).message ||
			'Erro ao atualizar atividade do plano de ação.'
		);
	}
};

const finalizeActivity = async (
	uuidDoPlanoDeAcao: string,
	idDaAtividade: string
): Promise<IActivity | Error> => {
	const now = new Date();
	const formattedDate = now.toISOString().slice(0, 10);

	const dataToFinalize: Partial<IActivity> = {
		finalizada: true,
		prazoRealInicio: formattedDate,
		prazoRealFim: formattedDate,
	};

	return updateActivity(idDaAtividade, dataToFinalize);
};

const deleteActivity = async (
	uuidDaAtividade: string
): Promise<void | Error> => {
	await AuthService.verify();
	try {
		const token = await AuthService.getAccessToken();

		await ApiBack.delete<void>(`/tarefa?uuid=${uuidDaAtividade}`, {
			headers: {
				Authorization: `${token}`,
			},
		});
	} catch (error) {
		return new Error(
			(error as { message: string }).message ||
			'Erro ao deletar atividade do plano de ação.'
		);
	}
};

const listPerDam = async (
	uuid_barragem: string
): Promise<any | Error> => {
	await AuthService.verify();
	try {
		const token = await AuthService.getAccessToken();
		const response = await ApiBack.get<any[]>(
			`/tarefas-barragem?uuid=${uuid_barragem}`,
			{
				headers: {
					Authorization: `${token}`,
				},
			}
		);

		if (response.data && Array.isArray(response.data)) {
			return response.data;
		}

		return [];
	} catch (error) {
		return new Error(
			(error as { message: string }).message ||
			'Erro ao listar atividades da barragem.'
		);
	}
};

export const ActionPlanActivitiesService = {
	getAll,
	create: createActivity,
	update: updateActivity,
	finalize: finalizeActivity,
	delete: deleteActivity,
	listPerDam: listPerDam
};
